"use client";

import { FC, useEffect, useMemo } from "react";
import { useKeyPressEvent } from "react-use";
import { usePathname } from "next/navigation";

import { MODALS } from "@Shared/config";
import { useModalState } from "@Shared/store";
import { ElProps } from "@Shared/types";
import { Icon } from "@Shared/ui";
import { twcx } from "@Shared/utils";

export type ContainerProps = ElProps<"div"> & {
    onOpen?: () => void;
    onClose?: () => void;
};

export const Container: FC<ContainerProps> = ({ className, onOpen, onClose, children, ...restProps }) => {
    const pathname = usePathname();
    const {
        activeModals,
        create,
        isOpen: modalIsOpen,
        close: modalClose,
    } = useModalState(["activeModals", "create", "isOpen", "close"]);

    const isOpen = useMemo(() => modalIsOpen(MODALS.info), [activeModals]);

    const close = () => modalClose(MODALS.info);

    useKeyPressEvent("Escape", () => (isOpen ? close() : null));

    useEffect(() => {
        create(MODALS.info);
    }, []);

    useEffect(() => {
        if (isOpen) {
            close();
        }
    }, [pathname]);

    useEffect(() => {
        if (isOpen) {
            onOpen?.();
        } else {
            onClose?.();
        }
    }, [isOpen]);

    return (
        <div
            className={twcx(
                "scroll-container-y fixed inset-0 z-40 flex items-start justify-center overflow-hidden bg-bg-black bg-opacity-50 p-4 transition-opacity",
                "md:p-6",
                {
                    "pointer-events-none opacity-0": !isOpen,
                },
                className
            )}
            onClick={close}
            {...restProps}
        >
            <div
                className={twcx(
                    "relative z-0 m-auto flex w-full flex-col justify-center gap-4 overflow-hidden rounded-[20px] bg-bg-black px-6 py-14 text-center text-white transition-[opacity,transform]",
                    "md:max-w-[400px] md:px-9 md:py-16",
                    "lg:max-w-[560px] lg:gap-5 lg:px-8 lg:py-20",
                    {
                        "scale-75 opacity-0": !isOpen,
                    }
                )}
                onClick={e => e.stopPropagation()}
            >
                <button
                    className={twcx(
                        "absolute right-[18px] top-[18px] z-10 flex size-10 items-center justify-center",
                        "md:right-[26px] md:top-[26px]",
                        "lg:transition-colors lg:[&:not(:disabled)]:hover:text-accent-1"
                    )}
                    type="button"
                    aria-label="Close modal info"
                    onClick={close}
                >
                    <Icon k="close" />
                </button>

                {children}
            </div>
        </div>
    );
};
